import type { IndicatorPeriod } from "@/lib/types";

// ---------------------------------------------------------------------------
// Helpers for displaying + entering indicator values per cadence.
//
// All dates flowing into/out of the API are still plain ISO strings
// (YYYY-MM-DD); these helpers just translate between that wire format and
// the period-shaped UI ("Q1 2026", "Mar 2026", "FY 2026", "Mar 14 2026").
//
// The backend snaps any date to its period start on save, so the UI just
// needs to round-trip the right canonical date.
// ---------------------------------------------------------------------------

/** "Q1 2026" / "Mar 2026" / "FY 2026" / "Mar 14, 2026" */
export function formatPeriod(date: string, period: IndicatorPeriod): string {
  if (!date) return "—";
  const d = new Date(date + "T00:00:00");
  const y = d.getFullYear();
  switch (period) {
    case "annual":
      return `FY ${y}`;
    case "quarterly": {
      const q = Math.floor(d.getMonth() / 3) + 1;
      return `Q${q} ${y}`;
    }
    case "monthly":
      return `${d.toLocaleDateString(undefined, { month: "short" })} ${y}`;
    default:
      return d.toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
  }
}

/** YYYY-MM-DD for the start of the period that contains the given date. */
export function snapDate(date: Date, period: IndicatorPeriod): string {
  const y = date.getFullYear();
  const m = date.getMonth();        // 0-indexed
  switch (period) {
    case "annual":
      return iso(y, 0, 1);
    case "quarterly":
      return iso(y, Math.floor(m / 3) * 3, 1);
    case "monthly":
      return iso(y, m, 1);
    default:
      return iso(y, m, date.getDate());
  }
}

/** Convert a year + quarter (1..4) → YYYY-MM-DD of the quarter's first day. */
export function quarterStartIso(year: number, quarter: number): string {
  return iso(year, (quarter - 1) * 3, 1);
}

/** Convert YYYY-MM-DD → { year, quarter } so the picker can pre-fill. */
export function quarterFromIso(s: string): { year: number; quarter: number } {
  const d = new Date(s + "T00:00:00");
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
}

/** "today" snapped to the period start. */
export function todayForPeriod(period: IndicatorPeriod): string {
  return snapDate(new Date(), period);
}

function iso(y: number, monthIdx: number, day: number): string {
  return `${y.toString().padStart(4, "0")}-${(monthIdx + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

/** A small year-window for year-pickers — current year ± 5. */
export function yearWindow(span = 5): number[] {
  const y = new Date().getFullYear();
  const out: number[] = [];
  for (let i = y - span; i <= y + span; i += 1) out.push(i);
  return out;
}

export const PERIOD_LABEL: Record<IndicatorPeriod, string> = {
  annual:    "Annual",
  quarterly: "Quarterly",
  monthly:   "Monthly",
  daily:     "Daily",
};

export const PERIOD_HINT: Record<IndicatorPeriod, string> = {
  annual:    "One number per year (FY).",
  quarterly: "One number per calendar quarter (Q1–Q4).",
  monthly:   "One number per month.",
  daily:     "One number per day — pick a date.",
};
